import BigNumber from 'bignumber.js';
import { formatUnits } from 'shared/lib/token/units';

import { formatNumber } from './numberFormatter';

export type FormatBalanceArgs = {
  maxDecimalDigits?: number;
  minDecimalDigits?: number;
  adaptiveDecimals?: boolean;
  maxTotalLength?: number;
  trimEllipsis?: boolean;
  decimals?: number;
  prettify?: boolean;
};

export const formatBalance = (
  balance: BigNumber | string | bigint = BigInt(0),
  {
    maxDecimalDigits = 4,
    maxTotalLength,
    adaptiveDecimals,
    minDecimalDigits = 2,
    trimEllipsis,
    decimals,
    prettify,
  }: FormatBalanceArgs = {},
) => {
  const actual = formatUnits(balance, decimals);
  let trimmed = actual;
  let isTrimmed = false;

  if (actual.includes('.')) {
    const parts = actual.split('.');
    const integer = parts[0];
    let decimal = parts[1];

    if (maxDecimalDigits > 0) {
      if (adaptiveDecimals) {
        const nonZeroIdx = decimal.split('').findIndex((v: string) => v !== '0');
        const sliceAt = Math.max(maxDecimalDigits, nonZeroIdx + 1);
        decimal = decimal.slice(0, sliceAt);
      } else {
        decimal = decimal.slice(0, maxDecimalDigits);
      }

      if (decimal.length < minDecimalDigits) {
        decimal = decimal.padEnd(minDecimalDigits, '0');
      }

      trimmed = `${integer}.${decimal}`;
      if (decimal.length < parts[1].length) {
        isTrimmed = true;
        if (trimEllipsis) trimmed += '...';
      }
    } else {
      trimmed = integer;
    }
  } else if (!isTrimmed && minDecimalDigits > 0 && trimmed === actual) {
    if (maxDecimalDigits > 0) {
      trimmed = `${actual}.${'0'.repeat(minDecimalDigits)}`;
    }
  }

  if (maxTotalLength && trimmed.length > maxTotalLength - 3) {
    if (trimmed[maxTotalLength - 4] === '.') {
      trimmed = trimmed.slice(0, maxTotalLength - 4);
    } else {
      isTrimmed = true;
      trimmed = `${trimmed.slice(0, maxTotalLength - 3)}${trimEllipsis ? '...' : ''}`;
    }

    if (!trimmed.includes('.') && !isTrimmed) {
      trimmed = `${trimmed}.${'0'.repeat(minDecimalDigits)}`;
    }
  }

  if (prettify && +trimmed !== 0) {
    const [integer, decimal] = trimmed.split('.');

    if (+integer !== 0) {
      const integerPrettified = formatNumber(integer);

      if (maxDecimalDigits) trimmed = `${integerPrettified}.${decimal}`;
      else trimmed = integerPrettified;
    }
  }

  return {
    actual,
    trimmed,
    isTrimmed,
  };
};

export const toDecimals = (balance: string | number, decimals = 18, shouldForamateNumber = false): string => {
  if (balance === '') {
    return '0';
  }

  if (typeof balance === 'number') {
    balance.toString();
  }

  const displayValue = new BigNumber(balance).multipliedBy(new BigNumber(10).pow(decimals));

  if (shouldForamateNumber) {
    return formatNumber(+displayValue.toString());
  }

  return displayValue.toString(10);
};

export const fromDecimals = (balance: string | number, decimals = 18): string => {
  if (balance === '') {
    return '0';
  }

  if (typeof balance === 'number') {
    balance.toString();
  }

  const displayValue = new BigNumber(balance).dividedBy(new BigNumber(10).pow(decimals));

  return parseFloat(displayValue.toFixed(4)).toString();
};

export const formatForDisplay = (balance: string | number, displayDecimals: number, withComas = false) => {
  const fixed = new BigNumber(balance).toFixed(displayDecimals, 1);

  if (withComas) {
    const parts = fixed.split('.');
    let integerPart = parts[0];
    const decimalPart = parts.length > 1 ? `.${parts[1]}` : '';

    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return integerPart + decimalPart;
  }

  return parseFloat(fixed).toString();
};

export const formatToken = (
  amount: number | string,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
  locale = 'en-US',
): string =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(+amount);
